/** biome-ignore-all lint/style/noNestedTernary: no nested ternary */
import cx from 'classnames';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import Skeleton from 'react-loading-skeleton';
import {useDispatch, useSelector} from 'react-redux';
import {useRouteMatch} from 'react-router-dom';
import {selectEventOptionEnablePhotoSharing} from '~/common/fabric/reducers/eviteEvent/selectors';
import {selectPhotosEnabled} from '~/common/fabric/reducers/giftingOptions/selectors';
import {useLazyGetPhotosQuery} from '~/ViewInvitation/api/endpoints/photos';
import {useImageProxySizer} from '~/ViewInvitation/hooks/useImageProxySizer';
import {usePhotos} from '~/ViewInvitation/hooks/usePhotos';
import {usePostPhoto} from '~/ViewInvitation/hooks/usePostPhoto';
import {
  MARK_SHARED_PHOTOS_PROCESSING,
  PHOTO_STATUS,
  UPDATE_SHARED_UPLOAD_PROGRESS,
} from '~/ViewInvitation/reducers/constants';
import {
  MESSAGE_STATES,
  ModuleMessaging,
} from '~/ViewInvitation/routes/Landing/PhotosModule/ModuleMessaging';
import {PhotoUploadButton} from '~/ViewInvitation/routes/Landing/PhotosModule/PhotoUploadButton';
import {PhotoGallery} from '~/ViewInvitation/routes/Photos/PhotoGallery/PhotoGallery';
import {PhotoGalleryEditor} from '~/ViewInvitation/routes/Photos/PhotoGalleryEditor/PhotoGalleryEditor';
import {PhotoGalleryUploadInput} from '~/ViewInvitation/routes/Photos/PhotoGalleryUploadInput';
import {PhotosModuleTile} from '~/ViewInvitation/routes/Photos/PhotosModuleTile/PhotosModuleTile';
import {PhotoViewer} from '~/ViewInvitation/routes/Photos/PhotoViewer/PhotoViewer';
import {selectEventIsFutureNonFuneral} from '~/ViewInvitation/selectors/eventDetails';
import {
  selectSharedUploadProcessing,
  selectSharedUploadProgress,
} from '~/ViewInvitation/selectors/photos';
import {eventHasPassed, isPreview} from '~/ViewInvitation/utils';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {ProgressAlert} from '~library/molecules/ProgressAlert';
import {LinkIcon} from '~library/svg/LinkIcon';
import {CheckmarkIcon} from '~library/svg/v2/CheckmarkIcon';
// import {selectEventType} from '~/common/fabric/reducers/eviteEvent/selectors'; // coming soon for category-based upsells

import {charcoal, feedbackSuccess, pebble, tanzanite, white} from '~libSass/base/_exports.sass';
import 'react-loading-skeleton/dist/skeleton.css';
import './PhotosModule.sass';

export const PHOTO_MODULE_PER_PAGE = 3;

const loadingState = (
  <div className="photos__carousel">
    <Skeleton className="photos__loading-skeleton" />
    <Skeleton className="photos__loading-skeleton" />
    <Skeleton className="photos__loading-skeleton" />
  </div>
);

export const PhotosModule = ({isCreationFlow, openPhotoGallery, openPhotoViewer}) => {
  const dispatch = useDispatch();
  const {getThumbnailUrl} = useImageProxySizer();
  const photosEnabledCreateFlow = useSelector(selectPhotosEnabled);
  const photosEnabledNotCreateFlow = useSelector(selectEventOptionEnablePhotoSharing);
  const photosEnabled = photosEnabledCreateFlow || photosEnabledNotCreateFlow;
  const [getPhotos] = useLazyGetPhotosQuery();
  const hasRsvpd = useSelector((state) => state.rsvp?.hasRsvpd ?? false);
  const rsvpOff = useSelector((state) => state.eventDetails.rsvpOff);
  const eventId = useSelector((state) => state.eventDetails?.eventId);
  const stagedPhotos = useSelector((state) => state.photos.photos);
  const eventIsFutureNonFuneral = useSelector(selectEventIsFutureNonFuneral);
  const [isPosting, setIsPosting] = useState(false);
  const [photoGalleryOpen, setPhotoGalleryOpen] = useState(openPhotoGallery);
  const [galleryEditorOpen, setGalleryEditorOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [photoViewerOpen, setPhotoViewerOpen] = useState(openPhotoViewer);
  const [photoViewerId, setPhotoViewerId] = useState(null);
  const sharedUploadProgress = useSelector(selectSharedUploadProgress);
  const sharedUploadProcessing = useSelector(selectSharedUploadProcessing);
  const [showProgress, setShowProgress] = useState(true);
  const [galleryFocusTrapPaused, setGalleryFocusTrapPaused] = useState(false);
  const searchParams = new URLSearchParams(window.location.search);
  const isCreateEditPreview = !!isPreview();
  const isGuestPreview = !!searchParams.get('preview');
  const isOnPhotoGalleryRoute = useRouteMatch('/photo-gallery');
  const isOnPhotoRoute = useRouteMatch('/photo');
  const isOnPhotosRoute = useRouteMatch('/photos');
  const unlockUI = eventHasPassed() || hasRsvpd || rsvpOff;
  const fileUploadRef = useRef();

  const pendingPhotos = stagedPhotos?.filter((p) => p.status === PHOTO_STATUS.PENDING);

  const {doPostPhotos} = usePostPhoto({setIsPosting, isPosting});

  const {
    photos,
    photoCount,
    isLoading: isLoadingPhotos,
  } = usePhotos({
    eventId,
    isHostModule: false,
    perPage: PHOTO_MODULE_PER_PAGE,
    includeCount: true,
  });

  const isLoading = isLoadingPhotos || isPosting;
  const showComingSoonState = photos?.length === 0 && eventIsFutureNonFuneral;

  const handlePost = useCallback(() => {
    setGalleryEditorOpen(false);
    dispatch({
      type: MARK_SHARED_PHOTOS_PROCESSING,
      payload: true,
    });
    doPostPhotos(pendingPhotos);
  }, [dispatch, pendingPhotos, doPostPhotos]);

  useEffect(() => {
    const successCount = stagedPhotos.filter((photo) => photo.status === 'success').length;
    const totalCount = stagedPhotos.length;
    const uploadPercent = totalCount > 0 ? (successCount / totalCount) * 100 : 0;

    dispatch({
      type: UPDATE_SHARED_UPLOAD_PROGRESS,
      payload: uploadPercent,
    });
    if (totalCount === 0) {
      dispatch({
        type: MARK_SHARED_PHOTOS_PROCESSING,
        payload: false,
      });
    }
  }, [dispatch, stagedPhotos]);

  const onViewGallery = useCallback(() => {
    setPhotoGalleryOpen(true);
  }, []);

  const onEditGallery = useCallback(() => {
    setGalleryEditorOpen(true);
    setPhotoGalleryOpen(false);
    setShowProgress(true);
  }, []);

  const onViewPhotoFn = useCallback(
    (photoId) => () => {
      setPhotoViewerId(photoId);
      setPhotoViewerOpen(true);
      setGalleryFocusTrapPaused(true);
    },
    []
  );

  const onClosePhotoViewer = useCallback(() => {
    setGalleryFocusTrapPaused(false);
    setPhotoViewerOpen(false);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    if (getPhotos && eventId) {
      const timeoutLength = isOnPhotosRoute || isOnPhotoGalleryRoute ? 0 : 3000;
      if (!isOnPhotoRoute) {
        const timeout = setTimeout(() => {
          getPhotos({
            eventId,
            perPage: -1,
            includeCount: true,
          });
        }, timeoutLength);
        return () => {
          clearTimeout(timeout);
        };
      }
    }
    return undefined;
  }, [eventId, getPhotos]);

  if (!photosEnabled && (!isCreateEditPreview || isGuestPreview)) {
    return null;
  }

  const messagingState = useMemo(() => {
    switch (true) {
      case showComingSoonState || isCreationFlow:
        return MESSAGE_STATES.PRE_EVENT;
      case !isLoading && !unlockUI:
        return MESSAGE_STATES.PRE_RSVP;
      case unlockUI && photos.length === 0:
      default:
        return MESSAGE_STATES.EMPTY;
    }
  }, [isCreationFlow, showComingSoonState, isLoading, unlockUI, photos]);

  const copyLinkToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/event/${eventId}/photos`);
      setLinkCopied(true);
      setTimeout(() => {
        setLinkCopied(false);
      }, 3000);
    } catch (e) {
      evite.error('Failed to copy to clipboard', e);
    }
  }, [eventId]);

  const showLoadingState = isLoading && unlockUI && !showComingSoonState;
  const showPhotos = !isLoading && unlockUI && photos.length > 0;
  const showAddPhotosBtn =
    (unlockUI && photos.length > 0) || messagingState === MESSAGE_STATES.EMPTY;

  return (
    <>
      <div className="photos__container">
        <div className="photos__header">
          <Typography variant="label2" data-qa-id="photos-module-title" color="pebble">
            Shared album
          </Typography>
          {showAddPhotosBtn &&
            (linkCopied
              ? <div className="photos__share-link">
                  <CheckmarkIcon color={feedbackSuccess} ratio={0.625} />
                  <Typography variant="paragraph3" color="feedbackSuccess">
                    Link copied!
                  </Typography>
                </div>
              : <Button
                  onClick={copyLinkToClipboard}
                  data-qa-id="photo-link-copy-to-keyboard"
                  variant="link"
                  className="photos__share-link"
                  disabled={isCreateEditPreview || showLoadingState}
                >
                  <LinkIcon color={showLoadingState ? pebble : tanzanite} ratio={0.75} />
                  Share
                </Button>)}
        </div>
        <div
          className={cx('photos__content', {
            'photos__content--has-photos': photos.length > 0 && unlockUI,
          })}
        >
          {showLoadingState
            ? loadingState
            : showPhotos
              ? <div className="photos__carousel" data-qa-id="photos-carousel">
                  {photos.map((photo, i) => (
                    <PhotosModuleTile
                      key={photo.id}
                      idx={i}
                      id={photo.id}
                      url={photo.content.url}
                      length={photos.length}
                      getThumbnailUrl={getThumbnailUrl}
                      onViewGallery={onViewGallery}
                      onViewPhotoFn={onViewPhotoFn}
                      isViewAll={i === 2 && photoCount > 3}
                      photos={photos}
                    />
                  ))}
                </div>
              : <ModuleMessaging messagingState={messagingState} />}
          {showAddPhotosBtn && (
            <PhotoUploadButton
              disabled={isCreationFlow || isLoading}
              fileUploadRef={fileUploadRef}
              iconColor={isCreationFlow || photos.length > 0 ? charcoal : white}
              fullWidth
              variant={photos.length > 0 && showPhotos ? 'secondary' : 'primary'}
            >
              Add photos
            </PhotoUploadButton>
          )}
        </div>
      </div>
      <PhotoGalleryEditor
        isOpen={galleryEditorOpen}
        onClose={() => setGalleryEditorOpen(false)}
        stagedPhotos={stagedPhotos}
        handlePost={handlePost}
        onViewPhotoFn={onViewPhotoFn}
        setPhotoGalleryOpen={setPhotoGalleryOpen}
        onEditGallery={onEditGallery}
      />
      {photoGalleryOpen && (
        <PhotoGallery
          isOpen={photoGalleryOpen}
          onClose={() => setPhotoGalleryOpen(false)}
          onEditGallery={onEditGallery}
          handlePost={handlePost}
          setPhotoGalleryOpen={setPhotoGalleryOpen}
          onViewPhotoFn={onViewPhotoFn}
          focusTrapPaused={galleryFocusTrapPaused}
          showComingSoonState={showComingSoonState}
        />
      )}
      {photoViewerOpen && (
        <PhotoViewer initialPhotoId={photoViewerId} onClose={onClosePhotoViewer} />
      )}
      {showProgress && sharedUploadProcessing && (
        <ProgressAlert
          progress={sharedUploadProgress}
          label="Uploading photos..."
          closeText="Dismiss"
          onClose={() => setShowProgress(false)}
          width={21}
        />
      )}
      {showProgress && sharedUploadProcessing && (
        <ProgressAlert
          progress={sharedUploadProgress}
          label="Uploading photos..."
          closeText="Dismiss"
          onClose={() => setShowProgress(false)}
          width={21}
        />
      )}
      <PhotoGalleryUploadInput
        fileUploadRef={fileUploadRef}
        collection="photos"
        setPhotoGalleryOpen={setPhotoGalleryOpen}
        onEditGallery={onEditGallery}
      />
    </>
  );
};
